<template>
  <Head title="Company Settings" />

  <div class="max-w-4xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Company Settings</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
            View your company information and preferences
          </p>
        </div>
        <Link v-if="canUpdate" :href="route('settings.company.edit')">
          <Button>
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Company
          </Button>
        </Link>
      </div>

      <!-- Company Logo -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Company Logo
          </h3>
        </template>
        <div class="p-6">
          <div class="flex items-center space-x-6">
            <div class="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                v-if="company.logo_url"
                :src="company.logo_url"
                :alt="company.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-3xl font-bold text-white">
                {{ company.name.charAt(0).toUpperCase() }}
              </span>
            </div>
            <div>
              <h4 class="text-lg font-medium text-gray-900 dark:text-gray-100">{{ company.name }}</h4>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">Company logo and branding</p>
            </div>
          </div>
        </div>
      </Card>

      <!-- Basic Information -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Basic Information
          </h3>
        </template>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Company Name</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.name || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Industry</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.industry_type || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Company Size</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.company_size || '-' }}</p>
            </div>
          </div>
        </div>
      </Card>

      <!-- Address -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Address
          </h3>
        </template>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Address Line 1</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.address_line_1 || '-' }}</p>
            </div>
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Address Line 2</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.address_line_2 || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">City</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.city || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">State / Province</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.state_province || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Postal Code</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.postal_code || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Country</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.country?.name || '-' }}</p>
            </div>
          </div>
        </div>
      </Card>

      <!-- Contact Information -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Contact Information
          </h3>
        </template>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Phone Number</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.phone || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Website</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                <a v-if="company.website" :href="company.website" target="_blank" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  {{ company.website }}
                </a>
                <span v-else>-</span>
              </p>
            </div>
          </div>
        </div>
      </Card>

      <!-- Localization -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Localization
          </h3>
        </template>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Currency</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {{ company.currency ? `${company.currency.code} (${company.currency.symbol}) - ${company.currency.name}` : '-' }}
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Timezone</label>
              <p class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ company.timezone?.display_name || company.timezone?.name || '-' }}</p>
            </div>
          </div>
        </div>
      </Card>

      <!-- External Request Portal -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            External Request Portal
          </h3>
        </template>
        <div class="p-6">
          <div class="space-y-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Share this portal link with external users to allow them to submit incidents, work orders, and helpdesk tickets without requiring an account.
              </p>
              <div class="flex items-center gap-3">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Portal URL</label>
                  <div class="flex items-center gap-2">
                    <input
                      type="text"
                      :value="portalUrl"
                      readonly
                      class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:outline-none"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      @click="copyPortalUrl"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {{ copied ? 'Copied!' : 'Copy' }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex items-center p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div :class="[
                'w-2 h-2 rounded-full mr-3',
                company.portal_enabled ? 'bg-green-500' : 'bg-gray-400'
              ]"></div>
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Portal Status: {{ company.portal_enabled ? 'Enabled' : 'Disabled' }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ company.portal_enabled ? 'External users can submit requests via this portal' : 'Portal is currently disabled' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
});

const { success, error } = useToast();
const copied = ref(false);

// Check permissions
const page = usePage();
const permissions = computed(() => page.props.auth?.permissions || []);
const canUpdate = computed(() => permissions.value.includes('system.settings.update'));

/**
 * Generate portal URL
 */
const portalUrl = computed(() => {
  if (!props.company.public_portal_uuid) {
    return 'Portal URL not available';
  }
  return `${window.location.origin}/external-request/${props.company.public_portal_uuid}`;
});

/**
 * Copy portal URL to clipboard
 */
const copyPortalUrl = async () => {
  try {
    await navigator.clipboard.writeText(portalUrl.value);
    copied.value = true;
    success('Portal URL copied to clipboard');
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (err) {
    error('Failed to copy URL to clipboard');
  }
};
</script>
